'use client';

import { usePulseTracker } from '@cineamo/pulse/src/react/PulseProvider';
import { trackedEntity } from '@cineamo/pulse/src/subject';
import { useTranslations } from 'next-intl';
import React, { useRef } from 'react';

import TicketButtonGrouped from '@/app/[locale]/(default-layout)/program/_components/TicketButtonGrouped';

import { ButtonWithIconStyle } from '@/components/buttons/ButtonWithIcon';
import ContentTitle from '@/components/ContentTitle';
import ListPlaceholderSimple from '@/components/list/ListPlaceholderSimple';
import ListLoadingAnimation from '@/components/ListLoadingAnimation';
import MovieDescription from '@/components/MovieDescription';

import { ShowingGroupedByDateTimeDto } from '@cineamo/api/models/showing/ShowingDto.types';
import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { Client } from '@cineamo/api/constants/Client.enum';

import { getGroupedShowings } from '@cineamo/ui/src/helpers/showing-helper';
import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import useShowingsListFiltered from '@/hooks/useShowingsListFiltered';
import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';

import MobileOrDesktopRenderer from '@cineamo/composables/src/MobileOrDesktopRenderer';
import { TrailerAndPosterCompositeImage } from '@cineamo/composables/src/TrailerAndPosterCompositeImage';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

import { ProgramDefaultListItemLayout } from './ProgramDefaultListItemLayout';
import { ProgramMobileListItemLayout } from './ProgramMobileListItemLayout';

type ShowingListItemProps = {
    content: CachedContentData;
    showingTag: ShowingTagDto;
};

export default function ShowingsForTagListItem(props: ShowingListItemProps) {
    const { content, showingTag } = props;
    const contentId = content.id;

    const { track: pulseTrack } = usePulseTracker();

    const imageComponent = (
        <TrailerAndPosterCompositeImage
            title={content.title}
            backdropImage={content.backdropImage}
            backdropBlurDataImage={content.backdropBlurDataImage}
            posterImage={content.posterImage}
            posterBlurDataImage={content.posterBlurDataImage}
            trailerUrl={content.trailerUrl}
            showPoster={content.showPoster}
            trackingData={{
                movieId: content?.movieId ?? ''
            }}
            onTrailerPlay={() => {
                pulseTrack({
                    ...trackedEntity({ movieId: content?.movieId, contentId }),
                    event_type: 'trailer_play'
                });
            }}
            onTrailerFinished={() => {
                pulseTrack({
                    ...trackedEntity({ movieId: content?.movieId, contentId }),
                    event_type: 'trailer_play_finished'
                });
            }}
        />
    );

    const href = {
        pathname: '/program/[contentId]',
        params: { contentId }
    } as HrefOrHrefWithParams<'/program/[contentId]'>;

    return (
        <MobileOrDesktopRenderer
            desktopContent={
                <ProgramDefaultListItemLayout
                    title={content.title ?? ''}
                    imageComponent={imageComponent}
                    href={href}
                    titleComponent={
                        <div className="h-full flex flex-col space-y-16">
                            <ContentTitle
                                content={content}
                                showGenres
                                showMovieDuration
                            />
                            <div className="flex-auto max-w-[600px]">
                                <MovieDescription
                                    content={content}
                                    maxLinesDesktop={6}
                                />
                            </div>
                        </div>
                    }
                    showingsSlider={
                        <ShowingSlider
                            content={content}
                            showingTag={showingTag}
                        />
                    }
                />
            }
            mobileContent={
                <ProgramMobileListItemLayout
                    href={href}
                    title={content.title ?? ''}
                    imageComponent={imageComponent}
                    titleComponent={
                        <div className="w-full">
                            <ContentTitle
                                content={content}
                                showGenres
                                showMovieDuration
                            />
                        </div>
                    }
                    showingsSlider={
                        <ShowingSlider
                            content={content}
                            showingTag={showingTag}
                        />
                    }
                />
            }
        />
    );
}

type ShowingSliderProps = {
    content: CachedContentData;
    showingTag: ShowingTagDto;
};

function ShowingSlider(props: ShowingSliderProps): React.JSX.Element {
    const { content, showingTag } = props;

    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { showings, isFetchingShowingList, noShowingsForSelectedCinemas } = useShowingsListFiltered({
        contentId: content.id,
        showingTagId: showingTag.id
    });

    const groupedShowings: ShowingGroupedByDateTimeDto[] = getGroupedShowings(showings, Client.CinemaWebsite);

    const { styleFadedState } = useScrollState({
        scrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        },
        refreshTrigger: groupedShowings.length
    });

    return (
        <div className="relative w-full h-[132px]">
            <div
                className="w-full h-full flex flex-row space-x-3 items-center overflow-x-auto no-scrollbar"
                ref={scrollContainerRef}
                style={styleFaded(styleFadedState, 10)}>
                {noShowingsForSelectedCinemas && <ListPlaceholderSimple text={t('program.no-showings-placeholder')} />}
                {groupedShowings.map((groupedShowing, index) => (
                    <TicketButtonGrouped
                        key={index}
                        content={content}
                        groupedShowing={groupedShowing}
                        className="w-[220px] h-full shrink-0"
                        style={{
                            backgroundColor: domainConfiguration.style.surfaceColor,
                            color: domainConfiguration.style.onSurfaceColor
                        }}
                        showDateAndTime
                        showTags
                    />
                ))}

                {isFetchingShowingList && <ListLoadingAnimation className="h-40 w-40" />}
            </div>
            {groupedShowings.length > 0 && (
                <BackAndForthButtonOverlay
                    scrollContainerRef={scrollContainerRef}
                    buttonWithIconStyle={ButtonWithIconStyle.transparent}
                    renderOutside
                />
            )}
        </div>
    );
}
